import { Component, OnInit, OnDestroy } from '@angular/core';
import { CurrencyPipe } from "@angular/common";
import { Router, ActivatedRoute, Data } from "@angular/router";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { minArrayLength, requiredNull } from "../../../common/models/form-validators";
import { Subscription, BehaviorSubject, of } from "rxjs";
import { map, finalize, switchMap, tap, debounceTime } from "rxjs/operators";
import { GridOptions, ColDef, IsRowSelectable, IRowNode, GridApi, RowSelectionOptions, SelectionColumnDef } from "ag-grid-community";

import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { AuthService } from "../../../services/auth.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { FlowInstanceService, FlowIndexSpec } from "../../../flow/services/flow-instance.service";
import { FlowIndex } from "../../../common/models/flow.dto";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { PaymentService } from "../../../payments/services/payment.service";
import { PaymentSearchCriteria, PaymentStatus, PaymentType, ApplicationPayment, PaymentRowDisplay, Payment, AdjustmentStep, PendingInstrument, PaymentRecordAdjustment, PaymentRecordAdjustmentNote, PaymentRecordAdjustmentDocument, PaymentRecordAdjustmentRequest, AdjustmentRequestStatus, writePendingToken } from "../../../payments/models/paymentItem.model";
import { IUploadFile } from '../../../common/services/document-management.service';
import { LoaderService } from "../../../common/services/loader.service";
import { OnBaseDocumentTypeProcessReference } from '../../../common/models/onBaseIntegrationAndMapping.model';

@Component({
    selector: 'app-request-payment-adjustment',
    templateUrl: './request-payment-adjustment.component.html',
    styleUrls: ['./request-payment-adjustment.component.scss'],
    standalone: false
})
export class RequestPaymentAdjustmentComponent extends CanDeactivateComponent implements OnInit, OnDestroy {



    adjustmentColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerName: "From Application Number",            
            field: "fromApplicationNumber",
            tooltipField: "fromApplicationNumber"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "To Application Number",
            field: "toApplicationNumber",
            tooltipField: "toApplicationNumber"
        } as ColDef,       
        {
            headerClass: "wrapped",
            headerName: "From Fee",
            field: "fromPaymentDescription",
            tooltipField: "fromPaymentDescription"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "To Fee",
            field: "toPaymentDescription",
            tooltipField: "toPaymentDescription"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "From Payment",
            field: "fromApplicationPaymentId",
            tooltipField: "fromApplicationPaymentId"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "To Payment",
            field: "toApplicationPaymentId",
            tooltipField: "toApplicationPaymentId"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Payment Instrument",
            field: "instrumentToken",
            tooltipField: "instrumentToken"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Amount",
            field: "amountApplied",
            valueFormatter: this.formatter.formatCurrency("amountApplied"),
            tooltipValueGetter: this.formatter.formatCurrency("amountApplied")
        } as ColDef
    ] as ColDef[];

	adjustmentGridOptions: GridOptions;
	adjustmentGridApi?: GridApi;

    applicationColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerName: "Application #",
            headerTooltip: "Application #",
            field: "flowIdentifier",
            tooltipField: "flowIdentifier",
            minWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Organization Name",
            headerTooltip: "Organization Name",
            field: "companyName",
            tooltipField: "companyName",
            minWidth: 150
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Organization #",
            headerTooltip: "Organization #",
            field: "organizationId",
            tooltipField: "organizationId",
            minWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Application Type",
            headerTooltip: "Application Type",
            field: "name",
            tooltipField: "name",
            minWidth: 150
        } as ColDef
    ];

    applicationGridOptions: GridOptions;
	applicationGridApi?: GridApi;
    
    applicationsToBePaid: PaymentRowDisplay[] = [];

    cannotApplyDialog: Dialog = new Dialog("Amount to be applied exceeds amount due for the fee.", [new DialogButton("Cancel", false, () => { return false; })]);

    currentPaymentColumns: ColDef[] = [
        {
            field: "applicationNumber",            
            rowGroup: true,
            hide: true,
            minWidth: 150,
            maxWidth: 150,
            headerName: "Application"
        } as ColDef,
        {
            field: "applicationPaymentId",
            rowGroup: true,
            hide: true,
            minWidth: 150,
            maxWidth: 150,
            headerName: "Payment Reference"
        } as ColDef,
        {
            headerName: "Payment",
            field: "paymentToken",
            minWidth: 200,
            sortable: false,
            filter: false
        } as ColDef,
        {
            field: "feeType",
            minWidth: 125,
            maxWidth: 200
        },
        {
            field: "feeAmount",
            valueFormatter: this.formatter.formatCurrency("feeAmount"),
            minWidth: 125,
            maxWidth: 125
        } as ColDef,
        {
            field: "amountPaid",
            valueFormatter: this.formatter.formatCurrency("amountPaid"),
            minWidth: 125,
            maxWidth: 125
        } as ColDef,
        {
            field: "amountDue",
            valueFormatter: this.formatter.formatCurrency("amountDue"),
            minWidth: 125,
            maxWidth: 125
        } as ColDef
       
    ];


	currentPaymentGridOptions: GridOptions;
	currentPaymentGridApi?: GridApi;

    currentStep: AdjustmentStep = AdjustmentStep.SelectApplicationAndPayments;

    get currentPayments(): PaymentRowDisplay[] {
        if (this.form == null || this.form.get("currentPayments") == null) return null;
        return <PaymentRowDisplay[]>this.form.get("currentPayments").value;
    }

    set currentPayments(payments: PaymentRowDisplay[]) {
        if (this.form == null || this.form.get("currentPayments") == null) return;
        this.form.get("currentPayments").setValue(payments);
    }

    get disableConfirm(): boolean {
        return this.pendingInstruments.find(i => i.hasError) != null || this.form.get('adjustedFees').invalid || this.form.get('pendingAdjustments').invalid;

    }

    get disableSubmit(): boolean {
        return this.form && this.form.invalid;
    }

    excludedTemplateIds: string[] = ['nEftdz7nEftgODcOy', 'nDQ0aPonDQ0fFq0elm']; //excludes the 'master' permit and registration flows from being shown

    get feeBeingAdjusted(): PaymentRowDisplay {
        if (this.form == null || this.form.get('feeBeingAdjusted') == null) return null;
        let record = <PaymentRowDisplay>this.form.get('feeBeingAdjusted').value
        if (record == null) return null;
        return record;
    }

    form: FormGroup;

    

    helpText: string = "In order to be eligible for adjustment, a payment must have been received either by cash, check or money order.  Credit and ACH payments are ineligible.";
    instrumentError: string = "Funds for this instrument have been over-allocated.";

    

    get matchingApplications(): FlowIndex[] {
        if (this.form == null || this.form.get('matchingApplications') == null) return null;
        return <FlowIndex[]>this.form.get('matchingApplications').value;
    }


    notes: PaymentRecordAdjustmentNote[] = [];

    onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];

    get paymentSearchText(): string {
        return `Finding payments for application #${this.selectedApplication}`;
    }

    get pendingAdjustments(): PaymentRecordAdjustment[] {
        if (this.form == null || this.form.get("pendingAdjustments") == null) return [];
        let value = this.form.get("pendingAdjustments").value;
        if (value == null) return [];
        return <PaymentRecordAdjustment[]>value;
    }

    get pendingInstruments(): PendingInstrument[] {
        if (this.form == null || this.form.get("availableInstruments") == null || this.form.get("availableInstruments").value == null) return [];
        return <PendingInstrument[]>this.form.get("availableInstruments").value;
        
    }

    pendingPaymentColumns: ColDef[] = [
        {
            field: "applicationNumber",
            minWidth: 140,
            maxWidth: 140,
            headerName: "Application",
            sortable: true,
            filter: true
        } as ColDef,
        {
            field: "applicationPaymentId",
            minWidth: 175,
            maxWidth: 175,
            headerName: "Payment Reference",           
            sortable: true,
            filter: true
        } as ColDef,
        {
            headerName: "Payment",
            field: "paymentToken",
            tooltipField: "paymentToken",
            minWidth: 175,            
            sortable: false,
            filter: false
        } as ColDef,
        {
            field: "feeType",
            tooltipField: "feeType",
            minWidth: 125,
            maxWidth: 225
        },
        {
            field: "feeAmount",
            valueFormatter: this.formatter.formatCurrency("feeAmount"),
            minWidth: 125,
            maxWidth: 125
        } as ColDef,
        {
            field: "amountPaid",
            valueFormatter: this.formatter.formatCurrency("amountPaid"),
            minWidth: 125,
            maxWidth: 125
        } as ColDef,
        {
            field: "amountDue",
            valueFormatter: this.formatter.formatCurrency("amountDue"),
            minWidth: 125,
            maxWidth: 125
        } as ColDef,
        {
            field: "paymentStatus",
            minWidth: 125,
            maxWidth: 125
        }
    ];

    

	pendingPaymentGridOptions: GridOptions;
	pendingPaymentGridApi?: GridApi;

    requestSubmittedDialog = new Dialog("The request to adjust payments has been submitted for approval.  For future reference, the request number is #[XXX].", [new DialogButton("OK", true, () => { this.exit(); })]);
    restartDialog: Dialog = new Dialog("Restarting will cause all previously entered information to be lost.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.reset(); }), new DialogButton("No", false, () => { return false; })]);

    get searchInProgress(): boolean {
        return this.searchingApplications.value == true || this.searchingAppliedPayments.value == true;
    }

    searchingApplications: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    searchingAppliedPayments: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    searchingUnpaidPayments: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get selectedApplication(): string {
        if (this.form == null || this.form.get('selectedApplication') == null) return "";
        let index = <FlowIndex>this.form.get('selectedApplication').value;
        if (index == null) return "";
        return index.flowIdentifier;
    }

    get selectedPayments(): Payment[] {
        if (this.form == null || this.form.get("paymentsToReapply") == null) return [];
        return <Payment[]>this.form.get('paymentsToReapply').value;
    }

    get selectedSource(): PendingInstrument {
        if (this.form == null || this.form.get("selectedSource") == null) return null;
        let sourceId = <string>this.form.get("selectedSource").value;
        let source = this.pendingInstruments.find(p => p.id == sourceId);
        return source;
    }

    get selectionColumnDef(): SelectionColumnDef {
        return this.formatter.defaultSelectionColumnDef;
    }

    steps = AdjustmentStep;

    get storedFiles(): IUploadFile[] {
        let returnValue: IUploadFile[] = [];
        if (this.form != null) {
            let fileCtrl = this.form.get("documentation");
            if (fileCtrl != null) {
                returnValue = <IUploadFile[]>fileCtrl.value;
            }
        }
        return returnValue;
    }

    reapplySubheader: SubHeader = new SubHeader({ title: "Request Payment Adjustment", buttons: [{ name: "Restart from the Beginning", method: () => { this.confirmRestart(); } }] });
    subheader: SubHeader = new SubHeader({ title: "Request Payment Adjustment", buttons: [{ name: "Restart", method: () => { this.confirmRestart(); } }] });
    subscription: Subscription = new Subscription();

    get undisbursedInstruments(): PendingInstrument[] {
        return this.pendingInstruments.filter(i => i.fundsRemaining > 0);
    }

    constructor(dialogService: DynamicDialogService, auth: AuthService, private subheaderService: SubHeaderService, private flowService: FlowInstanceService, private pipe: CurrencyPipe,
        private builder: FormBuilder, private formatter: GridFormatterService, private paymentService: PaymentService, private loader: LoaderService, private router: Router, private route: ActivatedRoute)
    {
		super(dialogService, auth);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    ngOnInit() {
        this.buildForm();

        let route$: Subscription = this.route.data.pipe(
            tap((data: Data) => {
                this.onBaseDocumentTypes = data.documentTypes;
            })
        ).subscribe({
            next: () => {              
            },
            error: (err) => { console.error(err); }
        });
        this.subscription.add(route$);

		this.applicationGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.applicationColumns, false, true, true, false);
		this.applicationGridOptions.rowSelection = {
            mode: 'singleRow',
            hideDisabledCheckboxes: true
        } as RowSelectionOptions;
        this.applicationGridOptions.onGridReady = (params) => { 
            this.applicationGridApi = params.api; 
            this.applicationGridApi.sizeColumnsToFit();
        };
        this.currentPaymentGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.currentPaymentColumns, false, true, true, false);
		this.currentPaymentGridOptions.groupDisplayType = "groupRows";
		this.currentPaymentGridOptions.onGridReady = (params) => { 
            this.currentPaymentGridApi = params.api; 
            this.currentPaymentGridApi.sizeColumnsToFit();
        };
        this.currentPaymentGridOptions.rowSelection = {
            mode: 'multiRow',
            headerCheckbox: true,
            selectAll: 'filtered',
            hideDisabledCheckboxes: true
        } as RowSelectionOptions;
        this.pendingPaymentGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.pendingPaymentColumns, false, true, true, true);
		this.pendingPaymentGridOptions.groupDisplayType = "groupRows";
		this.pendingPaymentGridOptions.onGridReady = (params) => { 
            this.pendingPaymentGridApi = params.api; 
            this.pendingPaymentGridApi.sizeColumnsToFit();
        };
		this.pendingPaymentGridOptions.rowSelection = {
            mode: 'singleRow',
            isRowSelectable: this.isRowSelectable(),
            hideDisabledCheckboxes: true
        } as RowSelectionOptions;
        this.adjustmentGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.adjustmentColumns, false, true, true, true);
		this.adjustmentGridOptions.onGridReady = (params) => { this.adjustmentGridApi = params.api; };
        this.subheaderService.updateSubHeader(this.subheader);
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })]);

        let appObs$ = this.form.get('selectedApplication').valueChanges.pipe(            
            switchMap((application: FlowIndex) => {
                if (application == null) return of([]);
                this.searchingUnpaidPayments.next(true);
                let spec: PaymentSearchCriteria = new PaymentSearchCriteria(application.organizationId, "", [PaymentStatus.Pending, PaymentStatus.Partial], []);
                return this.paymentService.search(spec);
            }),
            map((payments: ApplicationPayment[]) => {
                let displays: PaymentRowDisplay[] = [];
                payments.forEach(p => {
                    if (p.paymentRecords.length > 0) {
                        p.paymentRecords.forEach(r => {
                            displays.push(new PaymentRowDisplay(p, r));
                        })
                    } else {
                        displays.push(new PaymentRowDisplay(p, null));
                    }                   
                })
                return displays;
            }),
        ).subscribe({
            next: (payments: PaymentRowDisplay[]) => {
                console.log("done searching for unpaid payments");
                this.applicationsToBePaid = payments;
                this.searchingUnpaidPayments.next(false);
            },
            error: (error) => {
                console.error(error);
                this.searchingUnpaidPayments.next(false);
            }
        });
        this.subscription.add(appObs$);

        let inputObs$ = this.form.get('applicationNumber').valueChanges.pipe(
            debounceTime(1000),
            tap((input: string) => {
                if (this.selectedApplication != null && this.selectedApplication != input) {
                    this.currentPayments = null;
                    this.form.get('selectedApplication').setValue(null);
                    this.form.get('matchingApplications').setValue(null);
                }
            })
        ).subscribe({
            next: () => { },
            error: (error) => { console.error(error); }
        });
        this.subscription.add(inputObs$);
            
    }

    addNote(note: PaymentRecordAdjustmentNote): void {
        if (this.notes == null) this.notes = [];
        this.notes.push(note);
    }

    applicationSelectionChanged(evt: any): void {
        const rows = this.applicationGridApi.getSelectedRows();
        let application: FlowIndex = null;
        if (rows.length == 1) application = <FlowIndex>rows[0];
        let appCtrl = this.form.get('selectedApplication');
        if (appCtrl != null) {
            appCtrl.setValue(application);
        }
    }

    private buildForm(): void {
        this.form = this.builder.group(
            {
                applicationNumber: ["", [Validators.required, Validators.minLength(15), Validators.maxLength(15)]],
                currentPayments: [null, []],
                matchingApplications: [null, []],
                selectedApplication: ["", [Validators.required]],
                paymentsToReapply: [[], minArrayLength(1)],
                availableInstruments:[[], minArrayLength(1)],
                feeBeingAdjusted: [null, [requiredNull()]],
                selectedSource: [null, [requiredNull()]],
                appliedAmount:[0, [Validators.min(0), Validators.max(0)]],
                adjustedFees: [[], minArrayLength(1)],
                pendingAdjustments: [[], minArrayLength(1)],
                documentation: [[], []]
            }            
        );
    }

    confirmRestart(): void {
        if (!this.searchInProgress) {
            this.restartDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.restartDialog);
        }
    }

    deleteNote(index: number): void {
        if (this.notes == null || index > this.notes.length || index === -1) return;
        this.notes.splice(index, 1);
    }

    exit(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(["/home"]);
    }

    generateAdjustment(amount: number, newFee: PaymentRowDisplay, instrument: PendingInstrument): void {
        let existingAdjustments = <PaymentRecordAdjustment[]>this.form.get('pendingAdjustments').value;
        let recordId: number = 0;
        let fromApplicationPaymentId: string = "";
        let fromPaymentDescription: string = "";
        let instrumentToken: string = "";
        let fundsApplied = instrument.appliedAmount;
        let paymentDate: Date;
        let currentPayment = this.currentPayments.find(p => p.applicationPaymentId == instrument.originalPayments[0].applicationPaymentId);
        if (fundsApplied == 0) {
            recordId = parseInt(instrument.id, 10);
            fromApplicationPaymentId = currentPayment.applicationPaymentId;
            instrumentToken = writePendingToken(amount, instrument);
            fromPaymentDescription = currentPayment.feeDescription;
            paymentDate = currentPayment.toPaymentRecord().paymentDate;
        } else {
            for (let i = 0; i < instrument.originalPayments.length; i++) {
                let remaining = instrument.originalPayments[i].instrumentAmount - fundsApplied;
                if (remaining >= 0) {
                    recordId = instrument.originalPayments[i].id;
                    fromApplicationPaymentId = instrument.originalPayments[i].applicationPaymentId;
                    instrumentToken = writePendingToken(amount, instrument),
                        fromPaymentDescription = currentPayment.feeDescription;
                    paymentDate = currentPayment.toPaymentRecord().paymentDate;
                } 
                if (recordId > 0 && fromApplicationPaymentId.length > 0) break;
            }
        }
        if (paymentDate == null) paymentDate = new Date();
        let record: Payment = newFee.toPaymentRecord();

        let adjustment: PaymentRecordAdjustment = {
            requestId: "",
            fromApplicationNumber: this.selectedApplication,
            toApplicationNumber: newFee.applicationNumber,
            paymentRecordId: recordId,
            amountApplied: amount,
            fromApplicationPaymentId: fromApplicationPaymentId,
            toApplicationPaymentId: newFee.applicationPaymentId,
            instrumentToken: instrumentToken,
            fromPaymentDescription: fromPaymentDescription,
            toPaymentDescription: newFee.feeDescription,
            tokenId: record != null ? record.tokenId : "",
            instrumentAmount: record != null ? record.instrumentAmount : 0,
            paymentType: record != null ? record.paymentType : "",
            paymentDate: paymentDate
        };
        instrument.applyFunds(amount);
        existingAdjustments.push(adjustment);
        this.form.get("pendingAdjustments").setValue(existingAdjustments);
    }

    isRowSelectable(): IsRowSelectable {
        return (node: IRowNode) => {
            let selectable: boolean = this.selectedPayments.length === 0 || this.undisbursedInstruments.length > 0;
            if (selectable == true && node.data != null) {
                let pmt = (<PaymentRowDisplay>node.data).toApplicationPayment();
                selectable = pmt.amountDue > 0 && pmt.paymentStatus !== PaymentStatus.Paid;
            }
            return selectable;
        }
    }

    logError(err: any): void {
        console.error(err);
        this.dialogService.showError();
    }

    onFileAdded(evt: IUploadFile): void {
        if ((evt.documentType == null || evt.documentType.length == 0) && this.onBaseDocumentTypes.length > 0) evt.documentType = this.onBaseDocumentTypes[0].onBaseDocumentTypeName;
        const filesCtrl = this.form.get("documentation");
        if (filesCtrl != null) {
            let filesList = filesCtrl.value;
            if (filesList == null) filesList = [];
            const isNew = filesList.findIndex(l => l.fileName === evt.fileName) === -1;
            if (isNew) {
                filesList.push(evt);
                filesCtrl.setValue(filesList);
            }
        }
    }

    onFileDeleted(evt: IUploadFile): void {
        const filesCtrl = this.form.get("documentation");
        if (filesCtrl != null && filesCtrl.value != null) {
            const filesList: IUploadFile[] = <IUploadFile[]>filesCtrl.value;
            const fileIndex = filesList.findIndex(l => l.fileName == evt.fileName);
            if (fileIndex != -1) {
                filesList.splice(fileIndex, 1);
                filesCtrl.setValue(filesList);
            }
        }
    }

    paymentSelectionChanged(evt: any): void {
        const rows = this.currentPaymentGridApi.getSelectedRows();
        let payments: Payment[] = [];
        if (rows.length > 0) {            
            rows.forEach(r => {
                let pmt = <PaymentRowDisplay>r;
                payments.push(pmt.toPaymentRecord());
            });
            this.form.get("payments")
        }
        this.form.get("paymentsToReapply").setValue(payments);
    }

    pendingPaymentSelectionChanged(evt: any): void {
        const rows = this.pendingPaymentGridApi.getSelectedRows();
        let payment: PaymentRowDisplay = null;
        if (rows.length > 0) payment = <PaymentRowDisplay>rows[0];
        this.form.get('feeBeingAdjusted').setValue(payment);
    }

    pushOriginalPayment(amount: number): void {
        let sourcePayment = this.currentPayments.find(p => p.recordId.toString() === this.selectedSource.id);
        let existingReapply = this.applicationsToBePaid.findIndex(a => a.applicationPaymentId === sourcePayment.applicationPaymentId);
        if (existingReapply !== -1) {
            this.applicationsToBePaid.splice(existingReapply, 1);
        }
        sourcePayment.applyInstrument(this.selectedSource, amount * -1);
        this.applicationsToBePaid.push(sourcePayment);
        this.pendingPaymentGridApi.setGridOption("rowData", this.applicationsToBePaid);
    }

    reapplyFunds(): void {
        let amount: any = this.form.get("appliedAmount").value;
        if (amount == null) return;
        amount = <number>amount;
        if (amount <= this.selectedSource.fundsRemaining) {            
            let adjustedFee = this.feeBeingAdjusted;
            adjustedFee.applyInstrument(this.selectedSource, amount);
            let currentFees = <PaymentRowDisplay[]>this.form.get('adjustedFees').value;
            currentFees.push(adjustedFee);
            this.form.get('adjustedFees').setValue(currentFees);
            this.generateAdjustment(amount, adjustedFee, this.selectedSource);            
            let index = this.applicationsToBePaid.findIndex(a => a.applicationPaymentId == adjustedFee.applicationPaymentId && a.recordId == adjustedFee.recordId);
            if (index != -1) {
                this.applicationsToBePaid.splice(index, 1, adjustedFee);
                if (this.pendingPaymentGridApi) this.pendingPaymentGridApi.setGridOption("rowData", this.applicationsToBePaid);
            }
            this.pushOriginalPayment(amount);
            this.form.get("appliedAmount").setValue(0);
            this.form.get("feeBeingAdjusted").setValue(null);
            
            this.form.get("selectedSource").setValue(null);
        } else {
            this.cannotApplyDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.cannotApplyDialog);
        }
        
    }

    reset(): void {
        this.form.reset();
        this.form.get("paymentsToReapply").setValue([]);
        this.form.get("availableInstruments").setValue([]);
        this.form.get("adjustedFees").setValue([]);
        this.form.get("pendingAdjustments").setValue([]);
        this.form.get("documentation").setValue([]);
        this.setStep(AdjustmentStep.SelectApplicationAndPayments);
        this.subheaderService.updateSubHeader(this.subheader);
        this.allowDeactivate.next(true);
    }

    searchApplications(): void {
        const spec = new FlowIndexSpec();
        spec.flowIdentifier = this.form.get('applicationNumber').value;
        this.searchingApplications.next(true);
        let obs$ = this.flowService.findFlowIndicies(spec).pipe(
            map((results: FlowIndex[]) => {
                return results.filter(r => this.excludedTemplateIds.includes(r.flowTemplateId) == false);
            }),
            finalize(() => {
                this.searchingApplications.next(false);
                if (this.applicationGridApi) this.applicationGridApi.sizeColumnsToFit();
            })
        ).subscribe(
            (filteredIndicies: FlowIndex[]) => {
                this.form.get('matchingApplications').setValue(filteredIndicies);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(obs$);
    }

    searchForPayments(): void {
        let appliedSpec = new PaymentSearchCriteria("", this.form.get('applicationNumber').value, [PaymentStatus.Paid, PaymentStatus.Partial], [PaymentType.LocalCheck, PaymentType.MoneyOrder, PaymentType.PaidCash]);
        this.searchingAppliedPayments.next(true);
        let appliedObs$ = this.paymentService.search(appliedSpec).pipe(
            map((payments: ApplicationPayment[]) => {
                let displays: PaymentRowDisplay[] = [];
                payments.forEach(p => {
                    p.paymentRecords.forEach(r => {
                        displays.push(new PaymentRowDisplay(p, r));
                    })
                })
                return displays;
            }),
            finalize(() => { this.searchingAppliedPayments.next(false); })
        ).subscribe(
            (payments: PaymentRowDisplay[]) => {
                this.form.get('currentPayments').setValue(payments);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(appliedObs$);
    }

    setStep(step: AdjustmentStep): void {
        if (step == AdjustmentStep.ReassignPayments) {
            if (this.pendingPaymentGridApi) this.pendingPaymentGridApi.sizeColumnsToFit();
            let instruments: PendingInstrument[] = [];
            this.selectedPayments.forEach(p => {
                let existingInstrument = instruments.find(i => i.instrumentAmount == p.instrumentAmount && i.tokenId == p.tokenId && i.instrumentType == p.paymentType);
                if (existingInstrument == null) {
                    instruments.push(new PendingInstrument(p, this.pipe));
                } else {
                    existingInstrument.fundsRemaining += p.amount;
                    existingInstrument.originalPayments.push(p);
                }               
            });           
            this.form.get("availableInstruments").setValue(instruments);
            this.subheaderService.updateSubHeader(this.reapplySubheader);
        } else {
            this.subheaderService.updateSubHeader(this.subheader);
        }
        
        this.currentStep = step;
    }

    submitRequest(): void {
        if (this.form.valid) {
            let request: PaymentRecordAdjustmentRequest = {
                requestId: "",
                requestedById: "",
                requestedByName: "",
                approvedById: "",
                approvedByName: "",
                approvedDate: null,
                paymentRecordAdjustmentDocuments : [],
                paymentRecordAdjustmentNotes: [],
                paymentRecordAdjustments: [],
                requestStatus: AdjustmentRequestStatus.Pending
            };
            let adjustments = this.form.get("pendingAdjustments");
            if (adjustments != null && adjustments.value != null) {
                let pendingAdjustments = <PaymentRecordAdjustment[]>adjustments.value;
                pendingAdjustments.forEach(a => request.paymentRecordAdjustments.push(a));
            }
            if (this.notes != null && this.notes.length > 0) {
                request.paymentRecordAdjustmentNotes = this.notes;
            }
            let documentsCtrl = this.form.get("documentation");
            if (documentsCtrl != null && documentsCtrl.value != null) {
                let documents: IUploadFile[] = <IUploadFile[]>documentsCtrl.value;
                documents.forEach(d => {
                    let nameParts = d.fileName.split('.');
                    let docRequest: PaymentRecordAdjustmentDocument = {
                        requestId: "",
                        blobUri: d.blobUri,
                        fileName: d.fileName,
                        extension: (nameParts.length >= 2) ? nameParts[nameParts.length -1] : "",
                        type: d.type,
                        uploadedById: "",
                        uploadedByName: "",
                        onBaseDocumentType: d.documentType
                    };
                    request.paymentRecordAdjustmentDocuments.push(docRequest);
                });
            }
            this.loader.toggleLoader(true);
            let obs$ = this.paymentService.requestPaymentAdjustment(request).pipe(
                finalize(() => { this.loader.toggleLoader(false); })
            ).subscribe(
                (req: PaymentRecordAdjustmentRequest) => {
                    this.requestSubmittedDialog.message = this.requestSubmittedDialog.message.replace('[XXX]', req.requestId);
                    this.requestSubmittedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.requestSubmittedDialog);
                },
                (error) => {
                    this.logError(error);
                }
            );
        }
    }

}
